// DashboardContainer.js — MERGE S1 (RoleManagement + permission-based) + S2 (AIChatHistory)
// + Deep link: ?sidebar=hidden (auto-collapse sidebar) & ?ai=open (auto-buka AI panel)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import { useAuth, logActivity, api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { ShieldAlert } from 'lucide-react';
import { ActiveDashboardContext } from '../context/ActiveDashboardContext';
import { AIPageContextProvider, useAIPage } from '../context/AIPageContext';
import { useDashboardData } from '../context/DashboardDataContext';
import Sidebar, { isCompactNav } from './Sidebar';
import Header from './Header';
import useIsMobile from '../hooks/useIsMobile';
import MobileNav from './mobile/MobileNav';
import MobileHome from './mobile/MobileHome';
import AIPanel from './AI/AIPanel';
import Overview from '../pages/Overview';
import AWSBillingDashboard from '../pages/AWSBillingDashboard';
import AWSCostDashboard from '../pages/AWSCostDashboard';
import AWSInventory from '../pages/AWSInventory';
import AwsPricing from '../pages/AwsPricing';
import AWSCloudTrail from '../pages/AWSCloudTrail';
import SolmanDashboard from '../pages/SolmanDashboard';
import SAPUsageDashboard from '../pages/SAPUsageDashboard';
import SAPUserMaintenance from '../pages/SAPUserMaintenance'; // [SAP User Mgmt] NEW — admin only
import SAPRoleMaintenance from '../pages/SAPRoleMaintenance'; // [SAP Roles] NEW — permission sap_roles (tahap 1 read-only)
import SapBasisMonitor from '../pages/SapBasisMonitor';      // [SAP Basis] NEW — permission sap_basis_mon
import SapLogonAudit from '../pages/SapLogonAudit';          // [SAP Audit] NEW — akses dari luar jaringan, permission sap_basis_mon
import LinuxLoginAudit from '../pages/LinuxLoginAudit';
import AwsEc2Health from '../pages/AwsEc2Health';        // [EC2 Health] tab Indomas & PIL
import { DocDms, DocMrReport, DocAtmosfer, DocProcessMap } from '../pages/DocPages';  // [Documentation] arsitektur & alur proyek (tab DMS Project)
import StockWatchlist from '../pages/StockWatchlist';      // [Stock] data saham IDX read-only (stock_data.db)
import ClaudeActivity from '../pages/ClaudeActivity';      // [Claude OTel] aktivitas member Claude (admin)
import SapRouterLog from '../pages/SapRouterLog';            // [SAProuter] NEW — penampil saprouter.log via SM69, permission sap_basis_mon
import TransportRequest from '../pages/TransportRequest';   // [Transport] NEW — permission transport
import SAPCustomizing from '../pages/SAPCustomizing';
import SAPConfiguration from '../pages/SAPConfiguration'; // [SAP Configuration] SPRO dipisah dari SAP Customizing (27-10-2026)   // [SAP Customizing] NEW — metadata exit/BAdI, permission sap_customizing
import SAPSourceViewer from '../pages/SAPSourceViewer'; // [ABAP Source Viewer] NEW — isi source ABAP (live), permission sap_source
import SAPNotes from '../pages/SAPNotes';                   // [SAP Note] NEW — katalog/arsip/tracking note (permission null)
import LogViewer from '../pages/LogViewer';
import UserManagement from '../pages/UserManagement';
import TableMaintenance from '../pages/TableMaintenance';
import Notes from '../pages/Notes';                  // [Notes] NEW — dipisah dari tab TableMaintenance
import NotesPlain from '../pages/NotesPlain';        // [Notes Plain] NEW — catatan tanpa indeks RAG
import RoleManagement from '../pages/RoleManagement';    // [S1] NEW
import AIChatHistory from '../pages/AIChatHistory';      // [S2] NEW
import AIDataSources from '../pages/AIDataSources';      // [S3] NEW — AI data source builder
import AIConfigPage from '../pages/AIConfigPage';        // [AI] NEW — config AI global (admin/delegasi)
import TelegramSettings from '../pages/TelegramSettings';
import TelegramChatLog from '../pages/TelegramChatLog';
import ShareLinks from '../pages/ShareLinks';        // [Share] NEW — admin only
import SessionLog from '../pages/SessionLog';        // [Session] NEW — admin only
import FileDeployPage from '../pages/FileDeployPage'; // [Deploy] NEW — admin only (upload .js/.jsx)
import EmployeeOvertime from '../pages/EmployeeOvertime'; // [HR] NEW — employee overtime
import AIWorkspace from '../pages/AIWorkspace';
import AICoding from '../pages/AICoding';   // [AI Coding] NEW — dipisah dari AI Workspace, permission ai_coding        // [AI] NEW — halaman AI mandiri
import AIActionsPage from '../pages/AIActionsPage';   // [AI Actions] NEW — aksi bahasa natural + approval
import M365Dashboard from '../pages/M365Dashboard';
import NetworkTopology from '../pages/NetworkTopology';
import PcHealth from '../pages/PcHealth';                // PC Health (INFRA) - on-demand PC troubleshooting
import SolmanDocuments from '../pages/SolmanDocuments';    // [M365] NEW — ringkasan lisensi Microsoft 365
import Sdp from '../pages/Sdp';                        // [SDP] NEW — ManageEngine ServiceDesk Plus Cloud
import AwsCostBriefPage from '../pages/AwsCostBriefPage'; // [Cost Brief] NEW — brief biaya AWS bulanan
import MonthlyReport from '../pages/MonthlyReport';    // [Report] NEW — dipisah dari tab Table Maintenance
import NotificationHistory from '../pages/NotificationHistory'; // push_log history (admin)
import { ROUTE_META } from '../routesMeta';            // metadata route murni (anti circular import)

// Wiring komponen per key. ROUTE_META (path/label/permission) ada di routesMeta.js.
// Tambah halaman baru: tambah 1 baris di routesMeta.js + 1 baris di sini.
const COMPONENTS = {
  overview:          Overview,
  aws_billing:       AWSBillingDashboard,
  aws_cost:          AWSCostDashboard,
  aws_cost_brief:    AwsCostBriefPage,
  aws_inventory:     AWSInventory,
  aws_cloudtrail:    AWSCloudTrail,
  aws_pricing:       AwsPricing,
  solman:            SolmanDashboard,
  sap_usage:         SAPUsageDashboard,
  sap_user_mgmt:     SAPUserMaintenance,
  sap_roles:         SAPRoleMaintenance,
  sap_basis_mon:     SapBasisMonitor,
  sap_logon_audit:   SapLogonAudit,
  linux_login_audit: LinuxLoginAudit,
  aws_ec2_health:    AwsEc2Health,
  doc_dms:           DocDms,
  doc_mr_report:     DocMrReport,
  doc_atmosfer:      DocAtmosfer,
  doc_process_map:   DocProcessMap,
  stock_watchlist:   StockWatchlist,
  claude_activity:   ClaudeActivity,
  sap_router_log:    SapRouterLog,
  sap_customizing:   SAPCustomizing,
  sap_config:        SAPConfiguration,
  sap_source:        SAPSourceViewer,
  transport:         TransportRequest,
  sap_notes:         SAPNotes,
  logs:              LogViewer,
  ai_history:        AIChatHistory,
  ai_datasources:    AIDataSources,
  ai_config_settings: AIConfigPage,
  ai_workspace:      AIWorkspace,
  ai_coding:         AICoding,
  ai_actions:        AIActionsPage,
  users:             UserManagement,
  roles:             RoleManagement,
  table_maintenance: TableMaintenance,
  monthly_report:    MonthlyReport,
  notes:             Notes,
  notes_plain:       NotesPlain,
  telegram:          TelegramSettings,
  telegram_log:      TelegramChatLog,
  share_links:       ShareLinks,
  sessions:          SessionLog,
  file_deploy:       FileDeployPage,
  employee_overtime: EmployeeOvertime,
  m365:              M365Dashboard,
  sdp:               Sdp,
  netmap:               NetworkTopology,
  pc_health:            PcHealth,
  solman_docs:          SolmanDocuments,
  notif_history:        NotificationHistory,
};

// ROUTES = metadata + komponen. Hanya route yang punya komponen yang dipakai.
export const ROUTES = ROUTE_META
  .filter(r => COMPONENTS[r.key])
  .map(r => ({ ...r, component: COMPONENTS[r.key] }));

// Panel "Akses Ditolak" — tampil saat user membuka URL dashboard yang tidak
// dia punya izinnya (mis. SAP user buka /dashboard/aws-cost via deep-link).
function AccessDenied({ route }) {
  const {
    lang,
    t
  } = useLang();
  const id       = lang !== 'en';
  const pageName = route?.label || (t('dashCtr.page'));
  return (
    <div className="h-full flex items-center justify-center p-6" style={{ minHeight: '100%' }}>
      <div className="max-w-md text-center">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mb-5">
          <ShieldAlert className="text-rose-500" style={{ width: 30, height: 30 }} />
        </div>
        <h2 className="font-display text-xl font-bold text-slate-800 mb-2">
          {t('dashCtr.accessDenied')}
        </h2>
        <p className="text-sm text-slate-500 leading-relaxed">
          {id ? (
            <>Anda tidak memiliki izin untuk membuka <span className="font-semibold text-slate-700">{pageName}</span>. Hubungi administrator bila Anda merasa seharusnya punya akses.</>
          ) : (
            <>You don't have permission to open <span className="font-semibold text-slate-700">{pageName}</span>. Contact your administrator if you believe you should have access.</>
          )}
        </p>
      </div>
    </div>
  );
}

function DashboardContainerInner() {
  const { user, hasAccess }  = useAuth();
  const isMobile             = useIsMobile();
  // Opened inside the Primary Dashboard Android app (WebView): native app draws header + nav.
  const inApp = typeof window !== 'undefined' && (window.__PD_APP__ || /[?&]app=1/.test(window.location.search) || (() => { try { return sessionStorage.getItem('pd_app') === '1'; } catch (e) { return false; } })());
  const { lang }             = useLang();   // untuk RTL parsial (Tahap 3): dir konten
  const location             = useLocation();
  const navigate             = useNavigate();
  const [searchParams]       = useSearchParams();
  const { setActive }        = useDashboardData();
  const { pageAI }           = useAIPage();   // context data live dari halaman (Opsi B)
  const [aiOpen, setAiOpen]  = useState(false);
  const prevPath             = useRef(null);

  // ── Sidebar collapsed state (diangkat ke sini agar bisa dikontrol via URL) ──
  const SIDEBAR_COLLAPSED_KEY = 'sb_collapsed';
  const readSidebarCollapsed = () => { try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch (e) { return false; } };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const sidebarInitialized                      = useRef(false);

  // ── [Citation] 'ai:goto-note' — klik sumber AI dari halaman LAIN → navigate
  //    SPA ke Notes dengan query param; Notes membaca param saat mount lalu
  //    scroll + highlight ke paragraf sumber. (In-page memakai 'ai:open-note'.)
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (d.noteId == null) return;
      const qs = new URLSearchParams({
        note: String(d.noteId),
        ntab: String(Number.isInteger(d.tab) ? d.tab : 0),
        hl: String(d.snippet || '').slice(0, 160),
      });
      navigate(`/dashboard/notes?${qs.toString()}`);
    };
    window.addEventListener('ai:goto-note', handler);
    return () => window.removeEventListener('ai:goto-note', handler);
  }, [navigate]);

  // ── [Citation] 'ai:goto-page' — dari modal sumber KB ke DASHBOARD asalnya.
  //    Berbeda dari 'ai:goto-note' yang tujuannya selalu Notes: di sini path dan
  //    query param datang dari pemeta di noteNav.js, karena dokumen KB bisa
  //    berasal dari dashboard mana pun.
  //
  //    Path DIVALIDASI terhadap ROUTES, bukan dipakai mentah. Detail event
  //    berasal dari kode kita sendiri hari ini, tapi navigate() dengan string
  //    sembarang adalah jenis lubang yang mudah terbuka belakangan tanpa ada
  //    yang menyadarinya.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      const path = String(d.path || '');
      if (!path || !ROUTES.some(r => r.path === path)) return;
      const params = (d.params && typeof d.params === 'object') ? d.params : {};
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && String(v) !== '') qs.set(k, String(v));
      });
      const query = qs.toString();
      navigate(query ? `${path}?${query}` : path);
    };
    window.addEventListener('ai:goto-page', handler);
    return () => window.removeEventListener('ai:goto-page', handler);
  }, [navigate]);

  // ── Routing: tentukan dashboard aktif ──
  // Cek exact match dulu, baru startsWith — cegah /telegram match /telegram-log
  const activeKey = (() => {
    const path = location.pathname;
    // 1. Coba exact match dulu
    const exact = ROUTES.find(r =>
      r.key === 'overview'
        ? path === '/dashboard' || path === '/dashboard/'
        : path === r.path
    );
    if (exact) return exact.key;
    // 2. Baru coba sub-path (untuk nested routes jika ada)
    const sub = ROUTES.find(r =>
      r.key !== 'overview' && path.startsWith(r.path + '/')
    );
    return sub?.key || 'overview';
  })();

  // ── Lazy mount: hanya mount dashboard yang sudah pernah dibuka ──────────────
  // Sebelumnya SEMUA komponen dashboard di-mount sekaligus (disembunyikan via
  // display:none), sehingga useEffect loadData tiap dashboard menembak SEMUA
  // endpoint /data saat login → puluhan request berat bersamaan. Dengan ini,
  // dashboard di-mount saat PERTAMA dibuka, lalu tetap hidup (state & data
  // dipertahankan) agar pindah tab tetap instan tanpa re-fetch.
  const [mountedKeys, setMountedKeys] = useState(() => new Set([activeKey]));
  useEffect(() => {
    setMountedKeys(prev => (prev.has(activeKey) ? prev : new Set(prev).add(activeKey)));
  }, [activeKey]);

  // ── Deep link: ?sidebar=hidden → auto-collapse sidebar ──
  useEffect(() => {
    const sidebarParam = searchParams.get('sidebar');
    if (sidebarParam === 'hidden') {
      setSidebarCollapsed(true);
    } else if (!sidebarInitialized.current) {
      setSidebarCollapsed(readSidebarCollapsed());
    }
    sidebarInitialized.current = true;
  }, [location.pathname]); // eslint-disable-line

  // ── Deep link: ?ai=open → auto-buka AI panel ──
  useEffect(() => {
    if (searchParams.get('ai') === 'open') setAiOpen(true);
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    const typeMap = { solman: 'solman', sap_usage: 'sap_usage', aws_cost: 'aws_cost', aws_billing: 'aws_billing' };
    if (typeMap[activeKey]) setActive(typeMap[activeKey]);
  }, [activeKey, setActive]);

  // ── AI Assistant tersedia bila dashboard aktif: (a) terdaftar SQL Mode di AI
  //    Data Sources, ATAU (b) halaman LIVE yang mengekspos ringkasan via
  //    AIPageContext (Opsi B). Tombol AI di Header disembunyikan bila tidak. ──
  const [sqlAvailable, setSqlAvailable] = useState(false);
  useEffect(() => {
    let alive = true;
    setSqlAvailable(false);
    if (!activeKey || activeKey === 'overview') return;
    api.get(`/api/ai/sql-schema?dashboard=${encodeURIComponent(activeKey)}`)
      .then(r => { if (alive) setSqlAvailable(r.data?.mode === 'sql'); })
      .catch(() => { if (alive) setSqlAvailable(false); });
    return () => { alive = false; };
  }, [activeKey]);

  // page-context valid hanya bila key-nya cocok dgn halaman aktif.
  const pageCtxActive = !!pageAI && pageAI.key === activeKey;
  // [Gerbang AI Chat] tanpa ai_chat tombol AI disembunyikan (admin lolos via hasAccess).
  const aiAvailable   = (sqlAvailable || pageCtxActive) && hasAccess("ai_chat");

  // Tutup panel AI bila pindah ke halaman yang tidak mendukung AI.
  useEffect(() => { if (!aiAvailable) setAiOpen(false); }, [aiAvailable]);

  useEffect(() => {
    if (!user) return;
    const currentPath = location.pathname;
    if (prevPath.current === currentPath) return;
    prevPath.current = currentPath;
    const route = ROUTES.find(r =>
      r.key === 'overview'
        ? currentPath === '/dashboard' || currentPath === '/dashboard/'
        : currentPath === r.path || currentPath.startsWith(r.path + '/')
    );
    if (route) logActivity('VIEW_DASHBOARD', `Membuka: ${route.label}`);
  }, [location.pathname, user]);

  const handleToggleAI = useCallback(() => {
    const next = !aiOpen;
    setAiOpen(next);
    if (next) logActivity('OPEN_AI', 'Membuka AI Assistant');
  }, [aiOpen]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // ── [Sidebar sync] Halaman AI Coding / AI Workspace punya sidebar riwayat
  //    sendiri; keduanya tidak boleh terbuka bersamaan dengan sidebar navigasi.
  //    Halaman-halaman itu dirender lewat peta ROUTES sehingga tidak bisa
  //    menerima prop dari sini — dipakai window CustomEvent, idiom yang sudah
  //    dipakai 'ai:goto-note' di atas. Lihat components/AI/useHistorySidebarSync.js.
  useEffect(() => {
    const onSet = (e) => {
      if (typeof e?.detail?.collapsed !== 'boolean') return;
      setSidebarCollapsed(e.detail.collapsed);
    };
    window.addEventListener('app:sidebar-collapse', onSet);
    return () => window.removeEventListener('app:sidebar-collapse', onSet);
  }, []);

  // Siaran BALIK setiap perubahan — termasuk yang berasal dari tombol Collapse
  // di Sidebar dan dari deep link ?sidebar=hidden. Halaman AI menutup riwayatnya
  // saat menerima collapsed=false. Halaman hanya bereaksi pada nilai false,
  // sedangkan yang dikirimnya selalu true, jadi tidak ada umpan balik berulang.
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0'); } catch (e) {}
    window.dispatchEvent(new CustomEvent('app:sidebar-state', {
      detail: { collapsed: sidebarCollapsed },
    }));
  }, [sidebarCollapsed]);

  // Route aktif & cek apakah user kekurangan izin untuk membukanya.
  // (hasAccess() selalu true untuk admin, jadi admin tak pernah kena ini.)
  const activeRoute  = ROUTES.find(r => r.key === activeKey);
  const accessDenied = !!activeRoute?.permission && !hasAccess(activeRoute.permission);

  // [Compact nav] non-admin with <= COMPACT_NAV_MAX pages: no sidebar, navigate via Overview.
  const compactNav = isCompactNav(user);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <ActiveDashboardContext.Provider value={activeKey}>
      <div className="flex h-screen bg-slate-50 overflow-hidden">
        {!isMobile && !compactNav && (
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
          />
        )}
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          {!(isMobile && (activeKey === 'overview' || inApp)) && <Header onToggleAI={handleToggleAI} aiOpen={aiOpen} aiAvailable={aiAvailable} compactNav={compactNav} />}
          <div className="flex flex-1 overflow-hidden">
            <main className="flex-1 overflow-y-auto min-w-0" dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  style={isMobile ? { paddingBottom: 'calc(60px + env(safe-area-inset-bottom))' } : undefined}>
              {accessDenied ? (
                <AccessDenied route={activeRoute} />
              ) : isMobile && activeKey === 'overview' ? (
                <MobileHome />
              ) : (
                ROUTES.map(({ key, component: Component, permission }) => {
                  if (permission && !hasAccess(permission)) return null;
                  if (!mountedKeys.has(key)) return null;   // belum pernah dibuka → jangan mount/fetch
                  if (isMobile && key === 'overview') return null; // Home mobile dipakai sbg ganti
                  const isActive = key === activeKey;
                  return (
                    <div key={key} style={{ display: isActive ? 'block' : 'none' }}>
                      <Component />
                    </div>
                  );
                })
              )}
            </main>
            <AIPanel
              open={aiOpen}
              onClose={() => setAiOpen(false)}
              defaultWidth={380}
              activeKey={activeKey}
              activeLabel={activeRoute?.label}
              pageContext={pageCtxActive ? pageAI : null}
            />
          </div>
        </div>
        {isMobile && !inApp && <MobileNav />}
      </div>
    </ActiveDashboardContext.Provider>
  );
}

// Wrapper: sediakan AIPageContextProvider agar halaman LIVE (non-SQL) bisa
// mengekspos ringkasan data ke AI Assistant (Opsi B), dan DashboardContainerInner
// bisa membaca context itu untuk gating tombol AI + payload.
export default function DashboardContainer() {
  return (
    <AIPageContextProvider>
      <DashboardContainerInner />
    </AIPageContextProvider>
  );
}
